import { StartRecoveryOffer } from '@/components/StartRecoveryOffer';
import { SessionHoldPanel } from '@/components/charging/SessionHoldPanel';
import { idleSummaryFacts } from '@evi/shared';
import { IdleSummaryLine } from '@evi/ui';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { idleText } from '@evi/shared';
import {
  ArrowLeft, Battery, Clock, DollarSign, Zap, MapPin, Loader2,
  ExternalLink, Download,
} from 'lucide-react';
import {
  fetchSession, fetchSessionPoints, sessionPointsNote, formatCents,
  type SessionPoints,
} from '@/lib/api';
import { useHomeSession } from '@/hooks/useHomeSession';
import { downloadSessionReceipt } from '@/lib/receipt-pdf';
import { sessionReceiptPath } from '@/lib/sessionReceiptLink';
import { sessionFeeLine } from '@/lib/sessionFee';
import { sessionPaymentNote } from '@/lib/sessionPayment';
import { sessionWalletFxLine } from '@/lib/sessionWalletFx';
import { formatMoneyExplicit } from '@/lib/money';
import { IdlePanel } from '@/components/charging/IdlePanel';
import { toast } from '@/store/toast';
import { useStreamAwareInterval } from '@/store/sessionStream';
import styles from './SessionDetailPage.module.css';

const COMPLETED_STATUSES = new Set<string>([
  'completed', 'stopped_by_user', 'stopped_by_system',
]);

interface Props { sessionId: string; onNavigate: (path: string) => void; receiptMode?: boolean }

const STATUS_LABELS: Record<string, string> = {
  active: 'Charging',
  completed: 'Completed',
  failed: 'Failed',
  stopped_by_user: 'Stopped (You)',
  stopped_by_system: 'Stopped (System)',
};

function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function SessionDetailPage({ sessionId, onNavigate, receiptMode = false }: Props) {
  const { t, i18n } = useTranslation('sessions');
  // C3.2 — stream-aware polling. When session is active AND SSE
  // stream is 'open', polling backs off to 60s (push covers state
  // changes). When session is active but stream is idle/errored,
  // polling stays at the 10s baseline. Ended idle sessions continue
  // polling until unplugging and settlement finish.
  const activeRefetchInterval = useStreamAwareInterval(10_000);
  const sessionQ = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => fetchSession(sessionId),
    refetchInterval: (query) =>
      query.state.data?.idle_view?.cable_connected || query.state.data?.idle_view?.settlement_pending ? 5000
        : query.state.data?.status === 'active' ? activeRefetchInterval : false,
  });

  // Real per-session points. Skipped for fleet-sponsored (always zero,
  // the copy branches on billed_to) and not-yet-completed sessions.
  const detailCompleted = sessionQ.data
    ? COMPLETED_STATUSES.has(sessionQ.data.status)
    : false;
  const billedToFleet = sessionQ.data?.billed_to === 'fleet';
  const pointsQ = useQuery<SessionPoints>({
    queryKey: ['session', sessionId, 'points'],
    queryFn: () => fetchSessionPoints(sessionId),
    enabled: detailCompleted && !billedToFleet,
    staleTime: 60_000,
  });

  const session = sessionQ.data;
  const isActive = session?.status === 'active';
  const cur = session?.cost?.currency ?? 'USD';
  const idle = session?.idle_view;
  const idleFacts = session ? idleSummaryFacts(session) : null;
  const idleOpen = !!idle && !idle.cable_removed_at && !idle.billing_closed;
  // The fixed-fee line: EVI's connection fee on a session that pinned
  // it at start, the tariff's "Session fee" on a legacy one.
  const feeLine = session ? sessionFeeLine(session) : null;
  // Paid only with money behind it; a start that never happened says so.
  const paymentNote = session ? sessionPaymentNote(session) : null;
  // A USD wallet that paid this CAD session: both amounts and the pinned
  // Bank of Canada rate (Warren, 2026-10-25T02:52:41Z, option 1).
  const walletFx = session ? sessionWalletFxLine(session) : null;
  // Home-session framing: a session at the caller's own home charger is
  // never billed by EVI, so the page shows the estimated utility cost
  // (their entered rate) instead of showcasing a $0 EVI charge.
  const home = useHomeSession(session);
  // The home electricity estimate is in the home's currency (item 3), the
  // same one Session complete shows it in; the session's when unknown.
  const homeCur = home.currency ?? cur;
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const handleDownloadReceipt = async (): Promise<void> => {
    if (!session || downloadingReceipt) return;
    setDownloadingReceipt(true);
    try {
      await downloadSessionReceipt(
        session,
        home.isHomeSession
          ? { estimateCents: home.estimateCents, rateSummary: home.rateSummary, currency: home.currency }
          : undefined,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('detail.toast.receiptFailed');
      toast(msg, { tone: 'error' });
    } finally {
      setDownloadingReceipt(false);
    }
  };

  if (sessionQ.isLoading || !session) {
    return (
      <div className={styles.page}>
        <button className={styles.back} onClick={() => onNavigate('/sessions')}>
          <ArrowLeft size={16} /> {t('detail.back')}
        </button>
        <div className={styles.loading}>
          <Loader2 size={32} className={styles.spin} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => onNavigate('/sessions')}>
        <ArrowLeft size={16} /> {t('detail.back')}
      </button>

      {session.status === 'failed' && <StartRecoveryOffer sessionId={sessionId} onNavigate={onNavigate} />}
      {/* Status hero */}
      {receiptMode && <header className={styles.section} data-session-receipt={session._id}>
        <h1>{t('detail.invoice.view')}</h1>
        <p>EV Initiative Inc.</p>
        <p><a href="mailto:support@evinitiative.com">support@evinitiative.com</a></p>
      </header>}
      {!home.isHomeSession && <IdlePanel session={session} onNavigate={onNavigate} />}
      <div className={`${styles.hero} ${isActive ? styles.heroActive : ''}`}>
        <span className={`${styles.statusBadge} ${styles[`s_${session.status}`] ?? ''}`}>
          {isActive && <span className={styles.dot} />}
          {t(`detail.status.${session.status}`, { defaultValue: STATUS_LABELS[session.status] ?? session.status })}
        </span>
        {/* Home sessions lead with the utility ESTIMATE, not a $0 EVI
            charge — EVI never bills home charging. */}
        <h1 className={styles.heroAmount}>
          {home.isHomeSession
            ? (home.estimateCents != null ? formatCents(Math.round(home.estimateCents), homeCur) : t('free'))
            : formatCents(idleOpen ? idle!.charging_total_cents : idle?.total_cents ?? session.cost?.total ?? 0, cur)}
        </h1>
        {idleOpen && <p>{idleText(i18n.language, 'chargingTotal')}. {idleText(i18n.language, 'pendingNote')}</p>}
        <p className={styles.heroSub}>{t('detail.delivered', { kwh: session.energy_kwh.toFixed(2) })}</p>
        {home.isHomeSession && home.estimateCents != null && (
          <p className={styles.heroEstNote}>{t('detail.estNote')}</p>
        )}
      </div>

      <SessionHoldPanel session={session} />
      {/* Meta cards */}
      <div className={styles.metaGrid}>
        <MetaCard icon={Battery} label={t('detail.meta.energy')} value={`${session.energy_kwh.toFixed(2)} kWh`} />
        <MetaCard icon={Clock} label={t('detail.meta.duration')} value={durationLabel(session.duration_minutes)} />
        <MetaCard icon={Zap} label={t('detail.meta.connector')} value={`#${session.connector_id}`} />
        {idleFacts && <div style={{ gridColumn: '1 / -1', padding: 12, background: 'var(--evi-surface-2)', borderRadius: 12 }}><IdleSummaryLine facts={idleFacts} locale={i18n.language} /></div>}
        {home.isHomeSession
          ? (
            <MetaCard
              icon={DollarSign}
              label={t('detail.meta.estCost')}
              value={home.estimateCents != null ? formatCents(Math.round(home.estimateCents), homeCur) : t('free')}
            />
          )
          : <MetaCard icon={DollarSign} label={idleOpen ? idleText(i18n.language, 'chargingTotal') : t('detail.meta.total')}
            value={formatCents(idleOpen ? idle!.charging_total_cents : idle?.total_cents ?? session.cost?.total ?? 0, cur)} />}
      </div>

      {/* Destination */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}><MapPin size={14} /> {t('detail.section.destination')}</h2>
        <p className={styles.locationName}>{session.location_name}</p>
        <p className={styles.locationCharger}>{session.charger_label} · {session.charger_identity}</p>
      </div>

      {/* Cost breakdown. Home sessions replace the EVI breakdown with
          the utility-estimate framing: EVI bills nothing here, so the
          number that matters is what their utility will bill them. */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{home.isHomeSession ? t('detail.section.homeCost') : t('detail.section.costBreakdown')}</h2>
        {home.isHomeSession ? (
          <>
            {home.estimateCents != null && (
              <div className={styles.breakdownList}>
                <div className={styles.breakdownTotal}>
                  <span>{t('detail.break.estUtilityCost')}</span>
                  <span>{formatCents(Math.round(home.estimateCents), homeCur)}</span>
                </div>
              </div>
            )}
            <p className={styles.paymentNote}>
              {home.estimateCents != null
                ? t('detail.home.note', { rate: home.rateSummary })
                : t('detail.home.noteNoRate')}
            </p>
          </>
        ) : (
          <div className={styles.breakdownList}>
            <BreakRow label={t('detail.break.energy')} amount={session.cost?.energy_cost ?? 0} cur={cur} />
            {idleFacts && <IdleSummaryLine facts={idleFacts} locale={i18n.language} />}
            {feeLine && (
              <BreakRow
                label={feeLine.kind === 'connection' ? t('detail.break.connectionFee') : t('detail.break.sessionFee')}
                amount={feeLine.cents}
                cur={cur}
              />
            )}
            {(idle?.tax_cents ?? session.cost?.tax ?? 0) > 0 && <BreakRow label={t('detail.break.tax')} amount={idle?.tax_cents ?? session.cost.tax} cur={cur} />}
            <div className={styles.breakdownTotal}>
              <span>{t('detail.break.total')}</span>
              <strong>{formatCents(idle?.total_cents ?? session.cost?.total ?? 0, cur)}</strong>
            </div>
          </div>
        )}
        {home.isHomeSession || idle ? null : paymentNote === 'paid' ? (
          <p className={styles.paymentNote}>{t('detail.payment.paidCardOnFile')}</p>
        ) : paymentNote === 'wallet' ? (
          <p className={styles.paymentNote}>{t('detail.payment.paidWallet')}</p>
        ) : paymentNote === 'pending' ? (
          <p className={styles.pendingNote}>{t('detail.payment.pending')}</p>
        ) : paymentNote === 'released' ? (
          <p className={styles.paymentNote}>{t('detail.payment.released')}</p>
        ) : paymentNote === 'no_charge' ? (
          <p className={styles.paymentNote}>{t('detail.payment.noCharge')}</p>
        ) : paymentNote === 'v1_paid' ? (
          <p className={styles.paymentNote}>{t('detail.payment.v1Paid')}</p>
        ) : paymentNote === 'v1_billed' ? (
          <p className={styles.paymentNote}>{t('detail.payment.v1Billed')}</p>
        ) : paymentNote === 'v1_collected' ? (
          <p className={styles.paymentNote}>{t('detail.payment.v1Collected')}</p>
        ) : null}
        {!home.isHomeSession && walletFx && (
          <p className={styles.paymentNote} data-wallet-fx={walletFx.rate}>
            {t('detail.payment.walletFx', {
              usd: formatMoneyExplicit(walletFx.usdCents, 'USD', i18n.language),
              cad: formatMoneyExplicit(walletFx.cadCents, 'CAD', i18n.language),
              rate: walletFx.rate,
              date: walletFx.date,
            })}
          </p>
        )}
        {/* Honest per-session points line: real value when earned,
            "fleet sponsored" when the fleet paid, never a blanket
            promise. Only on completed sessions. */}
        {COMPLETED_STATUSES.has(session.status) && (
          <p className={styles.paymentNote}>
            {sessionPointsNote(true, session.billed_to === 'fleet', pointsQ.isLoading, pointsQ.data)}
          </p>
        )}
        {/* Every completed session has its own receipt, including
            wallet and free sessions. Authorization-release refunds
            are not charging receipts. */}
        {COMPLETED_STATUSES.has(session.status) && !receiptMode && (
          <button
            type="button"
            className={styles.invoiceBtn}
            onClick={() => {
              onNavigate(sessionReceiptPath(session._id));
            }}
          >
            {t('detail.invoice.view')} <ExternalLink size={12} />
          </button>
        )}
        {/* Download Receipt — client-side pdf-lib render. Available
            on every completed session, regardless of cost. Fleet
            drivers + zero-cost sessions (covered, coupon-zeroed,
            free-network) still need receipts for accounting — a
            $0 receipt is a legitimate audit artifact, not noise.
            Fleet sessions surface "Billed to {fleet_name}" in the
            payment line rather than a personal-card row. */}
        {COMPLETED_STATUSES.has(session.status) && (
          <button
            type="button"
            className={styles.invoiceBtn}
            disabled={downloadingReceipt}
            onClick={() => { void handleDownloadReceipt(); }}
          >
            {downloadingReceipt ? t('detail.invoice.generating') : <>{t('detail.invoice.download')} <Download size={12} /></>}
          </button>
        )}
      </div>

      {/* Meter timeline */}
      {session.meter_values && session.meter_values.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('detail.meter.title', { count: session.meter_values.length })}</h2>
          <div className={styles.meterList}>
            {session.meter_values.slice(-30).reverse().map((mv, i) => (
              <div key={i} className={styles.meterRow}>
                <span className={styles.meterTime}>{new Date(mv.timestamp).toLocaleTimeString()}</span>
                <span className={styles.meterEnergy}>{mv.energy_kwh.toFixed(3)} kWh</span>
                <span className={styles.meterPower}>{mv.power_kw.toFixed(2)} kW</span>
                {mv.soc_percent !== undefined && <span className={styles.meterSoc}>{mv.soc_percent.toFixed(0)}%</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session meta */}
      <div className={styles.metaSection}>
        <div><span>{t('detail.info.started')}</span><span>{new Date(session.started_at).toLocaleString()}</span></div>
        {session.ended_at && <div><span>{t('detail.info.ended')}</span><span>{new Date(session.ended_at).toLocaleString()}</span></div>}
        {session.rfid_tag && <div><span>{t('detail.info.rfidTag')}</span><span className={styles.mono}>{session.rfid_tag}</span></div>}
        <div><span>{t('detail.info.sessionId')}</span><span className={styles.mono}>{sessionId}</span></div>
        <div><span>{t('detail.info.transactionId')}</span><span className={styles.mono}>#{session.transaction_id}</span></div>
      </div>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ size: number }>; label: string; value: string }) {
  return (
    <div className={styles.metaCard}>
      <div className={styles.metaIcon}><Icon size={16} /></div>
      <p className={styles.metaLabel}>{label}</p>
      <p className={styles.metaValue}>{value}</p>
    </div>
  );
}

function BreakRow({ label, amount, cur }: { label: string; amount: number; cur: string }) {
  return (
    <div className={styles.breakRow}>
      <span>{label}</span>
      <strong>{formatCents(amount, cur)}</strong>
    </div>
  );
}
